import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, User, X } from "lucide-react";
import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import ConfigurationStep from "@pages/Contents/V2/Corporate/Catalog/Enrollment/steps/ConfigurationStep";
import { useAttributesList } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useAttributesList";
import { buildCommonConfig } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useEnrollmentSubmit";
import {
  buildInitialConfig,
  getDisabledEnrollTypes,
  validateConfig,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/config/enrollmentFieldsConfig";
import {
  EnrollmentConfigState,
  EnrollmentTriggerPayload,
  EnrollType,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import { EnrolledUserItem } from "../types";

interface EditEnrollmentModalProps {
  open: boolean;
  onClose: () => void;
  corporateId: string | number;
  objectId: number;
  objectType: CatalogEntityType;
  objectTitle?: string;
  user: EnrolledUserItem | null;
  onSuccess?: () => void;
}

const NO_EXPIRATION_SENTINEL = "9999-12-31";

const ENROLL_TYPE_PRIORITY: Record<EnrollType, number> = {
  TOP_MANDATORY: 0,
  TOP: 1,
  AUTO: 2,
  BOTTOM: 3,
  PRE_AUTO: 4,
  PRE_BOTTOM: 5,
};

export const disabledByPriority = (current?: EnrollType): EnrollType[] => {
  if (!current || ENROLL_TYPE_PRIORITY[current] === undefined) return [];
  return (Object.keys(ENROLL_TYPE_PRIORITY) as EnrollType[]).filter(
    (et) => ENROLL_TYPE_PRIORITY[et] >= ENROLL_TYPE_PRIORITY[current],
  );
};

export const buildConfigFromEnrollment = (
  user: EnrolledUserItem,
): EnrollmentConfigState => {
  const hasExpirationDate =
    !!user.expirationDate && user.expirationDate !== NO_EXPIRATION_SENTINEL;
  const hasDays = user.daysToComplete != null;
  const enableExpiration = hasExpirationDate || hasDays;

  return {
    ...buildInitialConfig(),
    enrollType: user.enrollType as EnrollType | undefined,
    enableExpiration,
    ...(enableExpiration
      ? {
          expirationKind: user.isExpirationFixed
            ? "fixed-date"
            : "completion-days",
          expirationType: user.isExpirationHard ? "hard" : "soft",
          ...(hasExpirationDate
            ? { expirationDate: user.expirationDate ?? undefined }
            : {}),
          ...(hasDays ? { daysToComplete: user.daysToComplete ?? 0 } : {}),
        }
      : {}),
    attributesIds: Array.isArray(user.attributes) ? user.attributes : [],
  };
};

const EditEnrollmentModal: React.FC<EditEnrollmentModalProps> = ({
  open,
  onClose,
  corporateId,
  objectId,
  objectType,
  objectTitle,
  user,
  onSuccess,
}) => {
  const { t } = useTranslation("users-and-groups");
  const theme = useTheme();

  const [config, setConfig] =
    useState<EnrollmentConfigState>(buildInitialConfig());
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (open && user) {
      setConfig(buildConfigFromEnrollment(user));
      setAttemptedSubmit(false);
    }
  }, [open, user]);

  const { options: attributes } = useAttributesList(String(corporateId));

  const trigger: EnrollmentTriggerPayload = {
    objectType,
    objectId,
    objectTitle,
    corporateId,
  };

  const { trigger: patchEnrollment, isMutating } = useSWRMutation(
    ServicesURL.patchEnrollment,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher<any>(url, MethodType.PATCH, arg),
  );

  const validation = validateConfig(config);

  const handleSave = async () => {
    setAttemptedSubmit(true);
    if (!validation.valid || !user?.userId) return;
    try {
      await patchEnrollment([
        {
          ...buildCommonConfig(config),
          corporateId: Number(corporateId),
          objectId,
          userId: [Number(user.userId)],
          isToj: false,
          mandatory: null,
        },
      ]);
      openToast({
        severity: "success",
        message: String(t("edit-modal.success")),
      });
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("edit-modal.error")),
      });
    }
  };

  const fullName = user
    ? [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim()
    : "";

  return (
    <Dialog
      open={open}
      onClose={isMutating ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                color: theme.palette.text.primary,
              }}
            >
              <User size={16} />
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {fullName || "-"}
              </Typography>
              {objectTitle && (
                <>
                  <Typography
                    sx={{ color: theme.palette.divider, fontSize: "14px" }}
                  >
                    |
                  </Typography>
                  <GraduationCap size={16} />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {objectTitle}
                  </Typography>
                </>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("edit-modal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isMutating}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "0 24px 8px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Box sx={{ pt: "16px" }}>
          <ConfigurationStep
            trigger={trigger}
            config={config}
            onChange={setConfig}
            attributes={attributes}
            errors={attemptedSubmit ? validation.errors : undefined}
            disabledEnrollTypes={Array.from(
              new Set([
                ...getDisabledEnrollTypes(trigger),
                ...disabledByPriority(
                  user?.enrollType as EnrollType | undefined,
                ),
              ]),
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px", gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isMutating}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("edit-modal.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isMutating}
          startIcon={isMutating ? <CircularProgress size={14} /> : undefined}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("edit-modal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEnrollmentModal;
